import { useTranslation } from "react-i18next";
import { Clock, Receipt, Users, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "@tabipla/ui";
import { WarittoLayout } from "../layouts/WarittoLayout";
import { useGroups } from "../../application/hooks/useGroups";
import { useRecentExpenses } from "../../application/hooks/useRecentExpenses";
import { EXPENSE_CATEGORIES } from "../../config/categories";
import { ExpensesListSkeleton } from "../components/Skeleton";

export function ActivityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: groups, isLoading: groupsLoading } = useGroups();
  const { data: recentExpenses, isLoading: expensesLoading } = useRecentExpenses();

  const isLoading = groupsLoading || expensesLoading;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t("activity.today", "今日");
    } else if (diffDays === 1) {
      return t("activity.yesterday", "昨日");
    } else if (diffDays < 7) {
      return t("activity.daysAgo", "{{days}}日前", { days: diffDays });
    } else {
      return date.toLocaleDateString("ja-JP", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const getCategoryCardStyle = (categoryKey: string) => {
    const categoryStyles: Record<string, string> = {
      food: "border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 dark:border-orange-800 dark:from-orange-950/30 dark:to-amber-950/30",
      transport:
        "border-blue-200 bg-gradient-to-r from-blue-50 to-sky-50 dark:border-blue-800 dark:from-blue-950/30 dark:to-sky-950/30",
      accommodation:
        "border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50 dark:border-purple-800 dark:from-purple-950/30 dark:to-violet-950/30",
      activity:
        "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:border-green-800 dark:from-green-950/30 dark:to-emerald-950/30",
      shopping:
        "border-pink-200 bg-gradient-to-r from-pink-50 to-rose-50 dark:border-pink-800 dark:from-pink-950/30 dark:to-rose-950/30",
      other: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50",
    };
    return categoryStyles[categoryKey] || categoryStyles.other;
  };

  const getCategoryIconStyle = (categoryKey: string) => {
    const iconStyles: Record<string, string> = {
      food: "bg-orange-500 shadow-lg shadow-orange-500/30",
      transport: "bg-blue-500 shadow-lg shadow-blue-500/30",
      accommodation: "bg-purple-500 shadow-lg shadow-purple-500/30",
      activity: "bg-green-500 shadow-lg shadow-green-500/30",
      shopping: "bg-pink-500 shadow-lg shadow-pink-500/30",
      other: "bg-gray-400 dark:bg-gray-600",
    };
    return iconStyles[categoryKey] || iconStyles.other;
  };

  if (isLoading) {
    return (
      <WarittoLayout title={t("nav.activity")}>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t("activity.title", "最近の履歴")}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {t("activity.subtitle", "出費の履歴を確認できます")}
            </p>
          </div>
          <ExpensesListSkeleton count={5} />
        </div>
      </WarittoLayout>
    );
  }

  const hasActivity = recentExpenses && recentExpenses.length > 0;

  return (
    <WarittoLayout title={t("nav.activity")}>
      <div className="animate-fade-in space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("activity.title", "最近の履歴")}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {t("activity.subtitle", "出費の履歴を確認できます")}
          </p>
        </div>

        {hasActivity ? (
          <div className="space-y-3">
            {recentExpenses.map((expense) => {
              const group = groups?.find((g) => g.id === expense.groupId);
              const category = EXPENSE_CATEGORIES[expense.category] || EXPENSE_CATEGORIES.other;
              const IconComponent = category.icon;
              const cardStyle = getCategoryCardStyle(expense.category);
              const iconStyle = getCategoryIconStyle(expense.category);

              return (
                <div
                  key={expense.id}
                  className={`cursor-pointer overflow-hidden rounded-xl border transition-all hover:shadow-lg active:scale-[0.98] ${cardStyle}`}
                  onClick={() => navigate(`/groups/${expense.groupId}`)}
                >
                  <div className="flex items-center gap-3 p-4">
                    {/* Category Icon */}
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconStyle}`}
                    >
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-gray-900 dark:text-white">
                        {expense.description}
                      </h3>
                      <p className="mt-0.5 text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                        {formatAmount(expense.amount)}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        {group && (
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {group.name}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(expense.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-8 dark:border-gray-700 dark:bg-gray-800/50">
            <EmptyState
              icon={Receipt}
              title={t("activity.noActivity", "履歴がありません")}
              description={t(
                "activity.noActivityDescription",
                "出費を追加すると、ここに表示されます"
              )}
            />
          </div>
        )}
      </div>
    </WarittoLayout>
  );
}
